import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CodeIcon } from "../../../assets/images/icons/CodeIcon";
import InfoIcon from "../../../assets/images/icons/InfoIcon.tsx";
import { showErrorToast } from "../../../utils/toast";
import BriaTooltip from "../BriaTooltip/BriaTooltip.tsx";
import CopyToClipboardButton from "../CopyToClipboardButton/CopyToClipboardButton";
import styles from "./RowJsonStructure.module.scss";

type Props = {
	title?: string;
	json: any;
	onUploadJson?: (obj: any) => void;
	onCopy?: () => void;
	onDownloadFileName?: string;
	disableUpload?: boolean;
	disableDownload?: boolean;
	uploadLoading?: boolean;
	hideUpload?: boolean;
	hideDownload?: boolean;
	hideTooltip?: boolean;
};

const RowJsonStructure = ({
	title = "View Row JSON Structure",
	json,
	onUploadJson,
	onCopy,
	onDownloadFileName = "visual_schema.json",
	disableUpload,
	disableDownload,
	uploadLoading,
	hideUpload,
	hideDownload,
	hideTooltip,
}: Props) => {
	const { t } = useTranslation("common", { keyPrefix: "rowJsonStructure" });
	const [expanded, setExpanded] = useState<boolean>(false);

	const pretty = useMemo(() => {
		try {
			const obj = typeof json === "string" ? JSON.parse(json) : (json ?? {});
			return JSON.stringify(obj, null, 2);
		} catch {
			return "{}";
		}
	}, [json]);

	const handleDownload = () => {
		const blob = new Blob([pretty], { type: "application/json" });
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = onDownloadFileName;
		link.click();
	};

	const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;
		try {
			const text = await file.text();
			const obj = JSON.parse(text);
			onUploadJson?.(obj);
		} catch (err) {
			showErrorToast(t("invalidJsonMessage"));
		}
	};

	return (
		<Box className={styles.container}>
			<Box className={styles.header} onClick={() => setExpanded((s) => !s)}>
				<Box className={styles.titleWrap}>
					<CodeIcon className={styles.codeIcon} />
					<Typography className={styles.title}>{title}</Typography>
					{!hideTooltip && (
						<BriaTooltip title={t("infoTooltip")}>
							<Box>
								<InfoIcon className={styles.infoIcon} />
							</Box>
						</BriaTooltip>
					)}
				</Box>
				<Box className={styles.chevron}>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Box>
			</Box>
			<Collapse in={expanded}>
				<Box className={styles.content}>
					<Box className={styles.contentHeader}>
						<Box />
						<Box className={styles.actions}>
							{!hideDownload && (
								<Tooltip title={t("download")}>
									<IconButton size="small" onClick={handleDownload} disabled={!!disableDownload}>
										<span className={styles.downloadIcon} />
									</IconButton>
								</Tooltip>
							)}
							{!hideUpload && (
								<Tooltip title={t("upload")}>
									<IconButton size="small" component="label" disabled={!!disableUpload}>
										{uploadLoading ? (
											<CircularProgress size={18} />
										) : (
											<span className={styles.uploadIcon} />
										)}
										<input
											hidden
											type="file"
											accept="application/json"
											onChange={handleUpload}
											disabled={!!disableUpload}
										/>
									</IconButton>
								</Tooltip>
							)}
							<BriaTooltip title={t("copy")}>
								<Box>
									<CopyToClipboardButton
										className={styles.copyIcon}
										buttonText=""
										tooltipText={t("copy")}
										getTextToCopy={async () => pretty}
										afterCopyAction={onCopy}
									/>
								</Box>
							</BriaTooltip>
						</Box>
					</Box>
					<pre className={styles.jsonBox}>{pretty}</pre>
				</Box>
			</Collapse>
		</Box>
	);
};

export default RowJsonStructure;
